import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, ExternalLink, Lock, History as HistoryIcon, Eye } from "lucide-react";
import HistoryDrawer, { ImportCsvButton } from "../../components/HistoryDrawer";
import { useSortedList } from "../../components/useSortedList";
import { useConfirm } from "../../components/Confirm";
import EntryDetailModal from "../../components/EntryDetailModal";
import ModalShell from "../../components/ModalShell";
import { useDataVersion } from "../../hooks/useAppShell";

const ADMIN_ONLY_EDIT = new Set(["artist_database","finance","released_songs","demo_submissions","legal_tracker","scouting","royalty_splits"]);
const STATUS_GATED = new Set(["scouting","demo_submissions","future_strategy"]);

export default function Generic({ user, dept, title, fields, statusOptions, linkedReleaseField, sheetEmbedField }) {
  const [entries, setEntries] = useState([]);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // entry being edited
  const [viewing, setViewing] = useState(null); // entry being viewed in detail modal
  const [historyFor, setHistoryFor] = useState(null);
  const [artistMap, setArtistMap] = useState({});
  const [releaseMap, setReleaseMap] = useState({});
  const confirm = useConfirm();
  const loc = useLocation();
  const highlightId = new URLSearchParams(loc.search).get("highlight");
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="row-${dept}-${highlightId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("entry-highlight");
        setTimeout(() => el.classList.remove("entry-highlight"), 2600);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [highlightId, entries, dept]);
  const isFeedback = dept === "feedback";
  const isAdmin = user.role === "admin";
  const isEditor = user.role === "editor";

  const canAdd = user.role !== "reader";
  const canEditRecord = isAdmin || (!ADMIN_ONLY_EDIT.has(dept) && isEditor);
  const canChangeStatus = isAdmin || (!STATUS_GATED.has(dept) && isEditor);

  const sortableFields = fields.filter(f => ["text", "number", "date", "select", "url", "artistSelect", "releaseSelect"].includes(f.type)).map(f => ({ key: f.key, label: f.label }));
  if (statusOptions) sortableFields.push({ key: "status", label: "Status" });
  const { sorted: sortedEntries, SortBar, sortBarProps, Pagination, paginationProps } = useSortedList(entries, sortableFields, "", { artistMap, releaseMap });

  const needsArtistResolve = fields.some(f => f.type === "artistSelect" || f.type === "multiArtistSelect");
  const needsReleaseResolve = fields.some(f => f.type === "releaseSelect");
  useEffect(() => {
    if (needsArtistResolve) {
      api.entries.list("artist_database").then(r => {
        const m = {};
        (r.entries || []).forEach(a => { m[a.id] = a.data?.name || a.id; });
        setArtistMap(m);
      }).catch((err) => console.error("Failed to load artist map:", err));
    }
    if (needsReleaseResolve) {
      api.linkedReleases().then(list => {
        const m = {};
        (list || []).forEach(r => { m[r.id] = r.title + (r.artist ? ` — ${r.artist}` : ""); });
        setReleaseMap(m);
      }).catch((err) => console.error("Failed to load release map:", err));
    }
  }, [dept, needsArtistResolve, needsReleaseResolve]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.entries.list(dept);
      setEntries(r.entries || []);
      setVersion(r.version || 0);
      // Also refresh artist & release resolver maps so newly-added artists/releases
      // in other modules show up here without a logout/login.
      if (needsArtistResolve) {
        try {
          const ar = await api.entries.list("artist_database");
          const m = {}; (ar.entries || []).forEach(a => { m[a.id] = a.data?.name || a.id; });
          setArtistMap(m);
        } catch (err) { console.error("artist refresh failed:", err); }
      }
      if (needsReleaseResolve) {
        try {
          const list = await api.linkedReleases();
          const m = {}; (list || []).forEach(rr => { m[rr.id] = rr.title + (rr.artist ? ` — ${rr.artist}` : ""); });
          setReleaseMap(m);
        } catch (err) { console.error("release refresh failed:", err); }
      }
    } catch (e) { toast.error(String(e.message || e)); }
    setLoading(false);
  }, [dept, needsArtistResolve, needsReleaseResolve]);
  const globalVersion = useDataVersion();
  useEffect(() => { load(); }, [load, globalVersion]);

  const create = async (data) => {
    try { await api.entries.create(dept, data); toast.success("Added"); setShowAdd(false); load(); }
    catch (e) { toast.error(String(e.message || e)); }
  };
  const save = async (id, data) => {
    try { await api.entries.update(dept, id, data, version); toast.success("Saved"); setEditing(null); load(); }
    catch (e) {
      if (String(e.message).includes("Someone else")) { toast.error("Someone else edited this. Refreshing…"); load(); }
      else toast.error(String(e.message || e));
    }
  };
  const remove = (id) => {
    confirm(
      "This entry will move to Recently Deleted, where you can restore it or delete it permanently within 15 days.",
      async () => {
        try { await api.entries.remove(dept, id); toast.success("Moved to Recently Deleted"); load(); }
        catch (e) { toast.error(String(e.message || e)); }
      },
      { title: "Delete this entry?" }
    );
  };
  const [cascadeRelease, setCascadeRelease] = useState(null); // demo → release planning
  const changeStatus = async (id, s) => {
    try {
      await api.entries.status(dept, id, s, version);
      toast.success("Status updated");
      if (dept === "demo_submissions" && s === "Approved") {
        const demo = entries.find(x => x.id === id);
        if (demo) setCascadeRelease({
          title: demo.data?.song || "",
          producer: demo.data?.producer || "",
          notes: demo.data?.notes || "",
        });
      }
      load();
    }
    catch (e) {
      if (String(e.message).includes("Someone else")) { load(); toast.error("Refreshed — try again"); }
      else toast.error(String(e.message || e));
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div className="muted" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" }}>Department</div>
          <h1 style={{ fontSize: 30, margin: "4px 0 0 0" }}>{title}</h1>
          {isFeedback && !isAdmin && <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>Submitted feedback is only visible to Admins — this is deliberate.</p>}
        </div>
        {canAdd && (
          <div style={{ display: "flex", gap: 10 }}>
            {isAdmin && <ImportCsvButton dept={dept} onDone={load} />}
            <button className="btn btn-primary" onClick={() => setShowAdd(true)} data-testid={`add-${dept}-btn`}>
              <Plus size={14} /> New entry
            </button>
          </div>
        )}
      </div>

      {showAdd && (
        <ModalShell title={`New ${title}`} subtitle="New Entry" onClose={() => setShowAdd(false)} testId="add-entry-modal">
          <EntryForm fields={fields} statusOptions={statusOptions} onCancel={() => setShowAdd(false)} onSubmit={create} title={`New ${title}`} user={user} dept={dept} linkedReleaseField={linkedReleaseField} embedded />
        </ModalShell>
      )}
      {editing && (
        <ModalShell title={`Edit ${title}`} subtitle="Editing" onClose={() => setEditing(null)} testId="edit-entry-modal">
          <EntryForm fields={fields} statusOptions={statusOptions} initial={editing.data} onCancel={() => setEditing(null)} onSubmit={(d) => save(editing.id, d)} title={`Edit`} user={user} dept={dept} isEdit linkedReleaseField={linkedReleaseField} embedded />
        </ModalShell>
      )}

      {sheetEmbedField && entries.some(e => e.data[sheetEmbedField]) && (
        <SheetEmbeds entries={entries} field={sheetEmbedField} />
      )}

      {entries.length > 0 && !(isFeedback && !isAdmin) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, gap: 12, flexWrap: "wrap" }}>
          <SortBar {...sortBarProps} />
          <span className="muted" style={{ fontSize: 11 }} data-testid="entry-count">{sortedEntries.length} entr{sortedEntries.length === 1 ? "y" : "ies"}</span>
        </div>
      )}

      <div className="panel" style={{ overflow: "hidden" }}>
        {loading ? <div style={{ padding: 20 }} className="muted">Loading…</div> :
         isFeedback && !isAdmin ? (
           <div style={{ padding: 40, textAlign: "center" }} className="muted">
             <MessageSquareIcon /> Your feedback is private. Submit anytime; only Admins can read it.
           </div>
        ) : entries.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center" }} className="muted">No entries yet.</div>
        ) : (
          <>
          <table>
            <thead>
              <tr>
                {dept === "released_songs" && <th style={{ width: 52 }}></th>}
                {fields.slice(0, 4).map(f => <th key={f.key}>{f.label}</th>)}
                {statusOptions && <th>Status</th>}
                <th style={{ textAlign: "right" }}></th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map(e => (
                <tr key={e.id} data-testid={`row-${dept}-${e.id}`}>
                  {dept === "released_songs" && (
                    <td>
                      {e.data?.cover_art_path ? (
                        <img
                          src={`${process.env.REACT_APP_BACKEND_URL}/api/files/covers/${e.data.cover_art_path}`}
                          alt="cover"
                          loading="lazy"
                          data-testid={`cover-thumb-${e.id}`}
                          style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover", display: "block" }}
                        />
                      ) : (
                        <div
                          data-testid={`cover-placeholder-${e.id}`}
                          style={{ width: 40, height: 40, borderRadius: 6, background: "var(--panel2)", border: "1px dashed var(--border)" }}
                        />
                      )}
                    </td>
                  )}
                  {fields.slice(0, 4).map(f => (
                    <td key={f.key}>{renderCell(e.data[f.key], f, { artistMap, releaseMap })}</td>
                  ))}
                  {statusOptions && (
                    <td>
                      {canChangeStatus ? (
                        <select className="input" style={{ maxWidth: 160 }} value={e.data.status || statusOptions[0]} onChange={(ev) => changeStatus(e.id, ev.target.value)} data-testid={`status-${e.id}`}>
                          {statusOptions.map(s => <option key={s}>{s}</option>)}
                        </select>
                      ) : (
                        <span className="badge" title="Only Admins can change status" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <Lock size={10} /> {e.data.status || statusOptions[0]}
                        </span>
                      )}
                    </td>
                  )}
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="btn" onClick={() => setViewing(e)} data-testid={`view-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }} title="View all fields">
                      <Eye size={12} />
                    </button>{" "}
                    <button className="btn" onClick={() => setHistoryFor(e.id)} data-testid={`history-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }} title="Change history">
                      <HistoryIcon size={12} />
                    </button>{" "}
                    {canEditRecord && (
                      <button className="btn" onClick={() => setEditing(e)} data-testid={`edit-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }}>
                        <Pencil size={12} />
                      </button>
                    )}{" "}
                    {isAdmin && (
                      <button className="btn btn-danger" onClick={() => remove(e.id)} data-testid={`del-${e.id}`} style={{ padding: "5px 9px", fontSize: 12 }}>
                        <Trash2 size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination {...paginationProps} />
          </>
        )}
      </div>
      {historyFor && <HistoryDrawer entryId={historyFor} onClose={() => setHistoryFor(null)} />}
      {cascadeRelease && (
        <ModalShell
          title="Send to Release Planning?"
          subtitle="Cascade — Demo Approved"
          onClose={() => setCascadeRelease(null)}
          testId="cascade-release-modal"
          maxWidth={520}
        >
          <p className="muted" style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
            This demo has been approved. Draft it as a Release Planning entry so the team can lock a release date and start prep.
          </p>
          <div className="panel" style={{ padding: 14, marginBottom: 18, background: "var(--panel-2, var(--panel2))" }}>
            <div style={{ fontSize: 12.5, marginBottom: 4 }}><b>Song:</b> {cascadeRelease.title || "—"}</div>
            <div className="muted" style={{ fontSize: 12 }}><b>From producer:</b> {cascadeRelease.producer || "—"}</div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button className="btn" data-testid="cascade-release-skip" onClick={() => setCascadeRelease(null)}>Skip for now</button>
            <button
              className="btn btn-primary"
              data-testid="cascade-release-open"
              onClick={async () => {
                try {
                  await api.entries.create("release_planning", {
                    title: cascadeRelease.title,
                    notes: cascadeRelease.notes,
                    status: "Planned",
                  }, 0);
                  toast.success("Draft added to Release Planning");
                  setCascadeRelease(null);
                } catch (e) { toast.error(String(e.message || e)); }
              }}
            >Draft in Release Planning</button>
          </div>
        </ModalShell>
      )}
      {viewing && (
        <EntryDetailModal
          entry={viewing}
          fields={fields}
          title={title}
          artistMap={artistMap}
          releaseMap={releaseMap}
          user={user}
          department={dept}
          onClose={() => setViewing(null)}
          onCoverChange={(eid, newPath) => {
            // Optimistically patch the local entries list + the modal itself so
            // the new thumbnail shows immediately without a full refetch.
            setEntries(prev => prev.map(e => e.id === eid ? { ...e, data: { ...e.data, cover_art_path: newPath } } : e));
            setViewing(v => v && v.id === eid ? { ...v, data: { ...v.data, cover_art_path: newPath } } : v);
          }}
        />
      )}
      {cascadeRelease && (
        <ModalShell
          title="Send to Release Planning?"
          subtitle="Cascade — Demo Approved"
          onClose={() => setCascadeRelease(null)}
          testId="cascade-release-modal"
          maxWidth={520}
        >
          <p className="muted" style={{ fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
            This demo has been approved. Draft it as a Release Planning entry so the team can lock a release date and start prep. The artist can be adjusted after creation from the Release Planning module.
          </p>
          <div className="panel" style={{ padding: 14, marginBottom: 18, background: "var(--panel2)" }}>
            <div style={{ fontSize: 12.5, marginBottom: 4 }}><b>Song:</b> {cascadeRelease.title || "—"}</div>
            <div className="muted" style={{ fontSize: 12 }}><b>From producer:</b> {cascadeRelease.artist_name || "—"}</div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button
              className="btn"
              data-testid="cascade-release-skip"
              onClick={() => setCascadeRelease(null)}
            >Skip for now</button>
            <button
              className="btn btn-primary"
              data-testid="cascade-release-open"
              onClick={async () => {
                try {
                  // Save a minimal Release Planning entry — user can flesh it out from there
                  await api.entries.create("release_planning", {
                    title: cascadeRelease.title,
                    notes: cascadeRelease.notes,
                    status: "Planned",
                  }, 0);
                  toast.success("Draft added to Release Planning");
                  setCascadeRelease(null);
                } catch (e) {
                  toast.error(String(e.message || e));
                }
              }}
            >Draft in Release Planning</button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

function renderCell(v, f, ctx = {}) {
  if (v == null || v === "") return f.type === "boolean" ? <span className="muted">No</span> : <span className="muted">—</span>;
  if (f.type === "boolean") return v ? <span className="badge badge-mint">Yes</span> : <span className="muted">No</span>;
  if (f.type === "url") return <a href={v} target="_blank" rel="noreferrer" style={{ color: "var(--mint)", display: "inline-flex", alignItems: "center", gap: 4 }}>Open <ExternalLink size={11} /></a>;
  if (f.type === "number") return typeof v === "number" ? v.toLocaleString("en-IN") : v;
  if (f.type === "artistSelect") return ctx.artistMap?.[v] || v;
  if (f.type === "releaseSelect") return ctx.releaseMap?.[v] || v;
  if (f.type === "multiArtistSelect" && Array.isArray(v)) {
    const names = v.map(id => ctx.artistMap?.[id] || id).filter(Boolean);
    return names.length > 3 ? `${names.slice(0, 3).join(", ")} +${names.length - 3}` : names.join(", ");
  }
  const s = String(v);
  return s.length > 60 ? s.slice(0, 60) + "…" : s;
}

function EntryForm({ fields, initial = {}, onSubmit, onCancel, title, user, dept, isEdit = false, linkedReleaseField, embedded = false }) {
  const [data, setData] = useState(initial);
  const [releases, setReleases] = useState([]);
  const [artists, setArtists] = useState([]);
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  const needsArtists = fields.some(f => f.type === "artistSelect" || f.type === "multiArtistSelect");
  const needsReleases = linkedReleaseField || fields.some(f => f.type === "releaseSelect");

  useEffect(() => {
    if (dept === "content_strategy" && !isEdit) set("ar_name", user.name);
    if (dept === "feedback" && !isEdit) set("submitted_by", user.name);
    if (needsReleases) api.linkedReleases().then(setReleases).catch((err) => console.error("Failed to load releases:", err));
    if (needsArtists) api.entries.list("artist_database").then(r => setArtists(r.entries || [])).catch((err) => console.error("Failed to load artists:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isYTorDK = dept === "finance" && (data.vendor === "YouTube" || data.vendor === "DistroKid") && data.type === "Revenue";

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...data };
    // Normalize multi-select comma fields for released songs
    ["artists","lyricists","mix_engineers"].forEach(k => {
      if (typeof payload[k] === "string") payload[k] = payload[k].split(",").map(s => s.trim()).filter(Boolean);
    });
    // Numbers
    fields.forEach(f => {
      if (f.type === "number" && payload[f.key] !== undefined && payload[f.key] !== "") payload[f.key] = Number(payload[f.key]);
      // Strip empty slots from multi-artist arrays before saving
      if (f.type === "multiArtistSelect" && Array.isArray(payload[f.key])) {
        payload[f.key] = payload[f.key].filter(Boolean);
      }
      // If a select isn't "Other" any more, drop the stale specify text
      if (f.type === "select" && payload[f.key] !== "Other") {
        delete payload[`${f.key}_other`];
      }
    });
    onSubmit(payload);
  };

  const usdKeys = new Set(["transaction_fees_usd","tax_withholding_usd","received_after_usd"]);

  return (
    <form
      onSubmit={submit}
      className={embedded ? "" : "panel"}
      style={embedded ? {} : { padding: 22, marginBottom: 20 }}
    >
      {!embedded && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ fontSize: 16 }}>{title}</h3>
          <button type="button" className="btn" onClick={onCancel}><X size={14} /></button>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
        {fields.map(f => {
          if (usdKeys.has(f.key) && !isYTorDK) return null;
          const val = data[f.key] ?? (Array.isArray(initial[f.key]) ? initial[f.key].join(", ") : initial[f.key] ?? "");
          const locked = f.locked;
          if (f.key === linkedReleaseField) {
            return (
              <label key={f.key} style={{ fontSize: 12 }}>
                <span className="muted" style={{ display: "block", marginBottom: 5 }}>{f.label}</span>
                <select className="input" value={val || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`}>
                  <option value="">— None —</option>
                  {releases.map(r => (
                    <option key={r.id + r.source} value={`${r.title}${r.artist ? ` — ${r.artist}` : ""}`}>
                      {r.title}{r.artist && ` — ${r.artist}`} · {r.status}
                    </option>
                  ))}
                </select>
              </label>
            );
          }
          if (f.type === "artistSelect") {
            return (
              <label key={f.key} style={{ fontSize: 12 }}>
                <span className="muted" style={{ display: "block", marginBottom: 5 }}>{f.label}{f.required && " *"}</span>
                <select className="input" required={f.required} value={val || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`}>
                  <option value="">— None —</option>
                  {artists.map(a => <option key={a.id} value={a.id}>{a.data?.name || a.id}</option>)}
                </select>
              </label>
            );
          }
          if (f.type === "releaseSelect") {
            return (
              <label key={f.key} style={{ fontSize: 12 }}>
                <span className="muted" style={{ display: "block", marginBottom: 5 }}>{f.label}{f.required && " *"}</span>
                <select className="input" required={f.required} value={val || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`}>
                  <option value="">— None —</option>
                  {releases.map(r => <option key={r.id + r.source} value={r.id}>{r.title}{r.artist && ` — ${r.artist}`}</option>)}
                </select>
              </label>
            );
          }
          if (f.type === "multiArtistSelect") {
            // Slot-based UX: each selected artist gets its own dropdown slot with
            // a delete button. A "+" button adds another slot. Each slot's dropdown
            // hides artists already picked in the OTHER slots of THIS field, so a
            // user can't accidentally add the same artist twice as (e.g.) "Artist".
            // Cross-field duplicates are fine — e.g. Bismil can be both Artist and
            // Lyricist because those live in different multiArtistSelect fields.
            const selected = Array.isArray(data[f.key]) ? data[f.key] : (data[f.key] ? [data[f.key]] : []);
            const updateAt = (i, id) => {
              const next = [...selected];
              if (!id) next.splice(i, 1);
              else next[i] = id;
              set(f.key, next);
            };
            const addSlot = () => set(f.key, [...selected, ""]);
            const removeAt = (i) => {
              const next = [...selected];
              next.splice(i, 1);
              set(f.key, next);
            };
            const slots = selected.length > 0 ? selected : [""];
            return (
              <label key={f.key} style={{ fontSize: 12, gridColumn: "1 / -1" }}>
                <span className="muted" style={{ display: "block", marginBottom: 5 }}>{f.label}{f.required && " *"}</span>
                {artists.length === 0 && (
                  <div className="muted" style={{ fontSize: 12, padding: 10, border: "1px dashed var(--border)", borderRadius: 8 }}>
                    No artists in the Artist Database yet — add one there first.
                  </div>
                )}
                {artists.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {slots.map((currentId, i) => {
                      // Artists already used in OTHER slots of this field (excluding this slot)
                      const usedElsewhere = new Set(selected.filter((_, idx) => idx !== i).filter(Boolean));
                      const options = artists.filter(a => a.id === currentId || !usedElsewhere.has(a.id));
                      return (
                        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          <select
                            className="input"
                            required={f.required && i === 0}
                            value={currentId || ""}
                            onChange={(e) => updateAt(i, e.target.value)}
                            data-testid={`field-${f.key}-${i}`}
                            style={{ flex: 1 }}
                          >
                            <option value="">— Select artist —</option>
                            {options.map(a => (
                              <option key={a.id} value={a.id}>
                                {a.data?.name || a.id}
                              </option>
                            ))}
                          </select>
                          {slots.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeAt(i)}
                              className="btn btn-danger"
                              data-testid={`remove-${f.key}-${i}`}
                              title="Remove"
                              style={{ padding: "6px 8px" }}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                    {selected.filter(Boolean).length < artists.length && (
                      <button
                        type="button"
                        onClick={addSlot}
                        className="btn"
                        data-testid={`add-slot-${f.key}`}
                        style={{ alignSelf: "flex-start", padding: "6px 12px", fontSize: 12 }}
                      >
                        <Plus size={12} /> Add another
                      </button>
                    )}
                  </div>
                )}
              </label>
            );
          }
          if (f.type === "select") {
            const isOther = String(val || "") === "Other";
            const otherVal = data[`${f.key}_other`] ?? initial[`${f.key}_other`] ?? "";
            return (
              <label key={f.key} style={{ fontSize: 12 }}>
                <span className="muted" style={{ display: "block", marginBottom: 5 }}>{f.label}{f.required && " *"}</span>
                <select className="input" required={f.required} value={val || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`}>
                  <option value="">Select…</option>
                  {f.options.map(o => <option key={o}>{o}</option>)}
                </select>
                {isOther && (
                  <input
                    className="input"
                    required
                    placeholder={`Specify ${f.label.toLowerCase()}…`}
                    value={otherVal}
                    onChange={(e) => set(`${f.key}_other`, e.target.value)}
                    data-testid={`field-${f.key}-other`}
                    style={{ marginTop: 8, fontSize: 13 }}
                  />
                )}
              </label>
            );
          }
          if (f.type === "textarea") {
            return (
              <label key={f.key} style={{ fontSize: 12, gridColumn: "1 / -1" }}>
                <span className="muted" style={{ display: "block", marginBottom: 5 }}>{f.label}{f.required && " *"}</span>
                <textarea className="input" rows={3} required={f.required} value={val || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`field-${f.key}`} />
              </label>
            );
          }
          if (f.type === "boolean") {
            return (
              <label key={f.key} style={{ fontSize: 12, gridColumn: "1 / -1", display: "flex", gap: 10, padding: "10px 12px", background: "var(--panel2)", border: "1px solid var(--border)", borderRadius: 10, cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.6 : 1 }}>
                <input
                  type="checkbox"
                  checked={!!val}
                  disabled={locked}
                  onChange={(e) => set(f.key, e.target.checked)}
                  data-testid={`field-${f.key}`}
                  style={{ marginTop: 3, cursor: locked ? "not-allowed" : "pointer" }}
                />
                <span>
                  <span style={{ fontWeight: 600, color: "var(--text)", display: "inline-flex", alignItems: "center", gap: 6 }}>{f.label}{locked && <Lock size={10} />}</span>
                  {f.hint && <span className="muted" style={{ display: "block", marginTop: 4, fontSize: 10.5, lineHeight: 1.5 }}>{f.hint}</span>}
                </span>
              </label>
            );
          }
          return (
            <label key={f.key} style={{ fontSize: 12 }}>
              <span className="muted" style={{ marginBottom: 5, display: "flex", alignItems: "center", gap: 6 }}>
                {f.label}{f.required && " *"}{locked && <Lock size={10} />}
              </span>
              <input
                className="input"
                type={f.type === "url" ? "url" : f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                required={f.required}
                disabled={locked}
                value={val ?? ""}
                onChange={(e) => set(f.key, e.target.value)}
                data-testid={`field-${f.key}`}
              />
              {f.hint && (
                <span className="muted" data-testid={`hint-${f.key}`} style={{ display: "block", marginTop: 4, fontSize: 10.5, lineHeight: 1.4 }}>{f.hint}</span>
              )}
            </label>
          );
        })}
      </div>
      <div style={{ marginTop: 18, display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" data-testid="submit-entry">{isEdit ? "Save changes" : "Add entry"}</button>
      </div>
    </form>
  );
}

function MessageSquareIcon() {
  return <div style={{ fontSize: 32, opacity: 0.4, marginBottom: 12 }}>✦</div>;
}

// Detects the URL provider and returns { embedUrl, providerLabel, canEmbed }.
// - Google Sheets: convert /edit to /preview (embeddable).
// - Canva design URLs (canva.com/design/{id}/...): convert to /view?embed (embeddable).
// - Canva short links (canva.link/...) and everything else: not embeddable →
//   we render a friendly "open in new tab" card instead of a broken iframe.
function resolveEmbed(url) {
  if (!url) return { embedUrl: null, providerLabel: "Link", canEmbed: false };
  try {
    const u = new URL(url);
    const host = u.hostname.toLowerCase();
    // Google Sheets
    if (host.includes("docs.google.com") && u.pathname.includes("/spreadsheets/")) {
      const embedUrl = url.replace(/\/edit(\?[^#]*)?$/, "/preview").replace(/\/edit#gid/, "/preview#gid");
      return { embedUrl, providerLabel: "Google Sheets", canEmbed: true };
    }
    // Google Docs / Slides also support /preview
    if (host.includes("docs.google.com")) {
      const embedUrl = url.replace(/\/edit(\?[^#]*)?$/, "/preview").replace(/\/edit#/, "/preview#");
      return { embedUrl, providerLabel: "Google Docs", canEmbed: true };
    }
    // Canva full design URL — canva.com/design/{id}/(edit|view)?...
    if (host.endsWith("canva.com")) {
      const m = u.pathname.match(/^\/design\/([^/]+)/);
      if (m) {
        return {
          embedUrl: `https://www.canva.com/design/${m[1]}/view?embed`,
          providerLabel: "Canva",
          canEmbed: true,
        };
      }
      return { embedUrl: null, providerLabel: "Canva", canEmbed: false };
    }
    // Canva short link — canva.link/... — we can't rewrite these without following
    // the redirect (which the browser can't do inside a cross-origin fetch), so
    // treat them as non-embeddable and show a friendly open card.
    if (host === "canva.link" || host.endsWith(".canva.link")) {
      return { embedUrl: null, providerLabel: "Canva", canEmbed: false };
    }
    // Fallback: unknown provider — don't try to iframe (most sites block it).
    return { embedUrl: null, providerLabel: host.replace(/^www\./, ""), canEmbed: false };
  } catch {
    return { embedUrl: null, providerLabel: "Link", canEmbed: false };
  }
}

function SheetEmbeds({ entries, field }) {
  const [openIdx, setOpenIdx] = useState(0);
  // Hide any entry that's been marked Completed — the label doesn't want to
  // wade through finished campaigns in the live calendar.
  const withLinks = entries.filter(e => e.data?.[field] && (e.data?.status || "").toLowerCase() !== "completed");
  if (withLinks.length === 0) return null;
  const safeIdx = openIdx < withLinks.length ? openIdx : 0;
  const current = withLinks[safeIdx];
  const rawUrl = current.data[field];
  const { embedUrl, providerLabel, canEmbed } = resolveEmbed(rawUrl);
  return (
    <div className="panel" style={{ padding: 18, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div>
          <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" }}>Asset Calendar (live)</div>
          <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{current.data.title || "Untitled"}</div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {withLinks.length > 1 && withLinks.map((e, i) => (
            <button key={e.id} onClick={() => setOpenIdx(i)} className={`btn ${i === safeIdx ? "btn-primary" : ""}`} style={{ padding: "5px 10px", fontSize: 11 }}>
              {(e.data.title || "Untitled").slice(0, 22)}
            </button>
          ))}
          <a href={rawUrl} target="_blank" rel="noreferrer" className="btn" style={{ padding: "5px 10px", fontSize: 11 }} data-testid="asset-open-external">
            <ExternalLink size={11} /> Open in {providerLabel}
          </a>
        </div>
      </div>
      {canEmbed ? (
        <iframe
          key={embedUrl}
          title="asset-calendar"
          src={embedUrl}
          style={{ width: "100%", height: 460, border: "1px solid var(--border)", borderRadius: 10, background: "#0d1210" }}
        />
      ) : (
        <div
          data-testid="asset-noembed-card"
          style={{
            width: "100%",
            minHeight: 260,
            border: "1px dashed var(--border)",
            borderRadius: 10,
            background: "var(--panel2)",
            padding: 40,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            gap: 14,
          }}
        >
          <ExternalLink size={26} style={{ color: "var(--mint)" }} />
          <div style={{ fontSize: 15, fontWeight: 600 }}>{providerLabel} won't embed inline</div>
          <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.6, maxWidth: 460 }}>
            {providerLabel === "Canva" && rawUrl && rawUrl.includes("canva.link")
              ? "Canva short links (canva.link/…) can't be embedded. Open the design in Canva, click Share → Copy link, and paste the full canva.com/design/… URL back into this entry to preview it here."
              : `${providerLabel} blocks inline preview from other sites. Click below to open it in a new tab.`}
          </div>
          <a
            href={rawUrl}
            target="_blank"
            rel="noreferrer"
            className="btn btn-primary"
            style={{ padding: "8px 16px", fontSize: 12.5, borderRadius: 10 }}
          >
            <ExternalLink size={13} /> Open in {providerLabel}
          </a>
        </div>
      )}
    </div>
  );
}
